import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  changeManuscriptEditorSelectedParagraphIndent,
  editableManuscriptContent,
  insertManuscriptEditorPlainTextAtSelection,
  manuscriptEditorBlockPresentation,
  manuscriptEditorStateFromEntry,
  manuscriptEditorTypographyAtOffset,
  manuscriptEntryFieldsFromEditorContent,
  mergeManuscriptEditorBlockWithPrevious,
  normalizeManuscriptEditorRuns,
  normalizeManuscriptEditorSurfaceSelection,
  replaceManuscriptEditorBlockRuns,
  replaceManuscriptEditorSurfaceSelection,
  setManuscriptEditorWholeDocumentAlignment,
  setManuscriptEditorSelectedParagraphAlignment,
  setManuscriptEditorSelectedParagraphLineSpacing,
  setManuscriptEditorSelectedParagraphPresentation,
  setManuscriptEditorSurfaceTypography,
  splitManuscriptEditorBlock,
  toggleManuscriptEditorSurfaceInlineMark,
} from "../utils/childrensBookManuscriptEditor";

import {
  MANUSCRIPT_FONT_FAMILIES,
  MANUSCRIPT_LINE_SPACING_VALUES,
} from "../utils/childrensBookManuscriptContent";

const ZERO_WIDTH = "\u200b";

const FONT_SIZE_OPTIONS = Object.freeze([
  8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72,
]);

const EDITOR_BOOK_DEFAULT_LINE_HEIGHT =
  1.65;

const PRODUCTION_BOOK_DEFAULT_FONT_SIZE_POINTS =
  11;

const PRODUCTION_BOOK_DEFAULT_LINE_HEIGHT_POINTS =
  14;

function manuscriptEditorPreviewLineHeight(
  lineSpacing
) {
  const explicit =
    Number(lineSpacing);

  if (
    !Number.isFinite(explicit) ||
    explicit <= 0
  ) {
    return EDITOR_BOOK_DEFAULT_LINE_HEIGHT;
  }

  const productionDefaultRatio =
    PRODUCTION_BOOK_DEFAULT_LINE_HEIGHT_POINTS /
    PRODUCTION_BOOK_DEFAULT_FONT_SIZE_POINTS;

  return (
    EDITOR_BOOK_DEFAULT_LINE_HEIGHT *
    (
      explicit /
      productionDefaultRatio
    )
  );
}

function defaultFontSizeForBlock(block) {
  if (block?.type !== "heading") {
    return 12;
  }

  if (block.level === 1) {
    return 24;
  }

  if (block.level === 2) {
    return 18;
  }

  return 14;
}

function nextFontSize(current, direction) {
  const value = Number(current) || 12;

  if (direction === "decrease") {
    const smaller = FONT_SIZE_OPTIONS
      .filter((size) => size < value)
      .pop();

    return smaller || FONT_SIZE_OPTIONS[0];
  }

  return FONT_SIZE_OPTIONS.find((size) => size > value) ||
    FONT_SIZE_OPTIONS[FONT_SIZE_OPTIONS.length - 1];
}

function cleanEditorText(value) {
  return String(value || "")
    .split(ZERO_WIDTH).join("")
    .replace(/\r\n?/g, "\n");
}

function styleFromElement(element, inherited) {
  const tag = String(element?.tagName || "").toLowerCase();
  const dataset = element?.dataset || {};
  const fontSize = Number(dataset.fontSize);

  return {
    bold:
      inherited.bold ||
      tag === "b" ||
      tag === "strong" ||
      dataset.bold === "true",
    italic:
      inherited.italic ||
      tag === "i" ||
      tag === "em" ||
      dataset.italic === "true",
    underline:
      inherited.underline ||
      tag === "u" ||
      dataset.underline === "true",
    fontFamily:
      dataset.fontFamily ||
      inherited.fontFamily ||
      "",
    fontSize:
      Number.isInteger(fontSize) && fontSize > 0
        ? fontSize
        : inherited.fontSize || 0,
  };
}

function runsFromEditorElement(element) {
  const runs = [];

  function append(text, marks) {
    const cleaned = cleanEditorText(text);

    if (!cleaned) {
      return;
    }

    const previous = runs[runs.length - 1];

    if (
      previous &&
      previous.bold === marks.bold &&
      previous.italic === marks.italic &&
      previous.underline === marks.underline &&
      String(previous.fontFamily || "") === String(marks.fontFamily || "") &&
      Number(previous.fontSize || 0) === Number(marks.fontSize || 0)
    ) {
      previous.text += cleaned;
      return;
    }

    runs.push({
      text: cleaned,
      bold: Boolean(marks.bold),
      italic: Boolean(marks.italic),
      underline: Boolean(marks.underline),
      ...(marks.fontFamily
        ? { fontFamily: marks.fontFamily }
        : {}),
      ...(Number.isInteger(marks.fontSize) && marks.fontSize > 0
        ? { fontSize: marks.fontSize }
        : {}),
    });
  }

  function visit(node, marks) {
    if (!node) {
      return;
    }

    if (node.nodeType === 3) {
      append(node.nodeValue, marks);
      return;
    }

    if (node.nodeType !== 1) {
      return;
    }

    const tag = String(node.tagName || "").toLowerCase();

    if (tag === "br") {
      append("\n", marks);
      return;
    }

    const nextMarks = styleFromElement(node, marks);
    const blockLike = ["div", "p", "li"].includes(tag);
    const hadText = runs.reduce(
      (sum, run) => sum + run.text.length,
      0
    );

    Array.from(node.childNodes || []).forEach((child) =>
      visit(child, nextMarks)
    );

    const hasText = runs.reduce(
      (sum, run) => sum + run.text.length,
      0
    );

    if (
      blockLike &&
      hasText > hadText &&
      !runs[runs.length - 1]?.text.endsWith("\n")
    ) {
      append("\n", nextMarks);
    }
  }

  Array.from(element?.childNodes || []).forEach((node) =>
    visit(node, {
      bold: false,
      italic: false,
      underline: false,
      fontFamily: "",
      fontSize: 0,
    })
  );

  if (runs.length && runs[runs.length - 1].text.endsWith("\n")) {
    runs[runs.length - 1].text = runs[runs.length - 1].text.slice(0, -1);
  }

  return normalizeManuscriptEditorRuns(runs);
}

function textLength(element) {
  return cleanEditorText(element?.innerText).length;
}

function selectedOffsets(element) {
  const selection = window.getSelection?.();

  if (
    !element ||
    !selection ||
    selection.rangeCount < 1 ||
    !element.contains(selection.anchorNode) ||
    !element.contains(selection.focusNode)
  ) {
    return null;
  }

  const anchorRange = document.createRange();
  anchorRange.selectNodeContents(element);
  anchorRange.setEnd(
    selection.anchorNode,
    selection.anchorOffset
  );

  const focusRange = document.createRange();
  focusRange.selectNodeContents(element);
  focusRange.setEnd(
    selection.focusNode,
    selection.focusOffset
  );

  const anchor = cleanEditorText(anchorRange.toString()).length;
  const focus = cleanEditorText(focusRange.toString()).length;

  return {
    start: Math.min(anchor, focus),
    end: Math.max(anchor, focus),
  };
}


/*
 * Describe the browser's live selection in manuscript block coordinates.
 *
 * Unlike selectedOffsets(), this adapter deliberately allows the anchor
 * and focus to live in different manuscript blocks. It does not mutate
 * the DOM or manuscript authority.
 */
export function manuscriptSurfaceDomSelection(surface) {
  const selection = window.getSelection?.();

  if (
    !surface ||
    !selection ||
    selection.rangeCount < 1 ||
    !surface.contains(selection.anchorNode) ||
    !surface.contains(selection.focusNode)
  ) {
    return null;
  }

  function elementForNode(node) {
    if (!node) {
      return null;
    }

    if (node.nodeType === 1) {
      return node;
    }

    return node.parentElement || null;
  }

  function manuscriptBlockForNode(node) {
    const element = elementForNode(node);
    const block = element?.closest?.(
      "[data-manuscript-block-id]"
    );

    return block && surface.contains(block)
      ? block
      : null;
  }

  function offsetInsideBlock(block, node, nodeOffset) {
    if (!block || !node) {
      return null;
    }

    const range = document.createRange();

    try {
      range.selectNodeContents(block);
      range.setEnd(node, nodeOffset);

      return cleanEditorText(
        range.toString()
      ).length;
    } catch {
      return null;
    }
  }

  const anchorBlock =
    manuscriptBlockForNode(
      selection.anchorNode
    );

  const focusBlock =
    manuscriptBlockForNode(
      selection.focusNode
    );

  if (!anchorBlock || !focusBlock) {
    return null;
  }

  const anchorOffset =
    offsetInsideBlock(
      anchorBlock,
      selection.anchorNode,
      selection.anchorOffset
    );

  const focusOffset =
    offsetInsideBlock(
      focusBlock,
      selection.focusNode,
      selection.focusOffset
    );

  if (
    anchorOffset === null ||
    focusOffset === null
  ) {
    return null;
  }

  return {
    anchorBlockId:
      String(
        anchorBlock.dataset
          ?.manuscriptBlockId || ""
      ),
    anchorOffset,

    focusBlockId:
      String(
        focusBlock.dataset
          ?.manuscriptBlockId || ""
      ),
    focusOffset,
  };
}


function selectionOffsets(element) {
  const selected = selectedOffsets(element);

  if (selected) {
    return selected;
  }

  const end = textLength(element);

  return {
    start: end,
    end,
  };
}

function locateTextOffset(root, requestedOffset) {
  const offset = Math.max(0, Number(requestedOffset) || 0);
  const walker = document.createTreeWalker(
    root,
    window.NodeFilter.SHOW_TEXT
  );
  let remaining = offset;
  let node = walker.nextNode();
  let lastNode = null;

  while (node) {
    lastNode = node;
    const length = cleanEditorText(node.nodeValue).length;

    if (remaining <= length) {
      return {
        node,
        offset: Math.min(remaining, node.nodeValue.length),
      };
    }

    remaining -= length;
    node = walker.nextNode();
  }

  if (lastNode) {
    return {
      node: lastNode,
      offset: lastNode.nodeValue.length,
    };
  }

  return {
    node: root,
    offset: 0,
  };
}


/*
 * Restore browser selection from manuscript block coordinates after
 * React has rerendered the continuous manuscript surface.
 *
 * Unlike restoreSelection(), the anchor and focus may live in
 * different manuscript blocks. Direction is preserved when the
 * browser exposes a directional Selection API. If it does not, the
 * fallback still restores the same selected range in document order.
 *
 * This function changes browser selection only. It does not change
 * manuscript content, block identity or editor authority.
 */
export function restoreManuscriptSurfaceDomSelection(
  surface,
  requestedSelection = {}
) {
  if (!surface) {
    return false;
  }

  const anchorBlockId =
    String(
      requestedSelection
        ?.anchorBlockId || ""
    );

  const focusBlockId =
    String(
      requestedSelection
        ?.focusBlockId || ""
    );

  if (
    !anchorBlockId ||
    !focusBlockId
  ) {
    return false;
  }

  const blocks =
    Array.from(
      surface.querySelectorAll(
        "[data-manuscript-block-id]"
      )
    );

  const anchorBlock =
    blocks.find(
      (block) =>
        String(
          block.dataset
            ?.manuscriptBlockId ||
            ""
        ) ===
        anchorBlockId
    );

  const focusBlock =
    blocks.find(
      (block) =>
        String(
          block.dataset
            ?.manuscriptBlockId ||
            ""
        ) ===
        focusBlockId
    );

  if (
    !anchorBlock ||
    !focusBlock
  ) {
    return false;
  }

  const anchor =
    locateTextOffset(
      anchorBlock,
      requestedSelection
        ?.anchorOffset
    );

  const focus =
    locateTextOffset(
      focusBlock,
      requestedSelection
        ?.focusOffset
    );

  const selection =
    window.getSelection?.();

  if (
    !selection ||
    !anchor?.node ||
    !focus?.node
  ) {
    return false;
  }

  try {
    surface.focus?.();
    selection.removeAllRanges();

    /*
     * Modern Chromium/Safari expose setBaseAndExtent(), which
     * preserves the original anchor/focus direction directly.
     */
    if (
      typeof selection.setBaseAndExtent ===
      "function"
    ) {
      selection.setBaseAndExtent(
        anchor.node,
        anchor.offset,
        focus.node,
        focus.offset
      );

      return true;
    }

    /*
     * extend() also preserves direction: establish the anchor as a
     * collapsed selection and extend it to the requested focus.
     */
    if (
      typeof selection.extend ===
      "function"
    ) {
      const collapsed =
        document.createRange();

      collapsed.setStart(
        anchor.node,
        anchor.offset
      );

      collapsed.collapse(true);

      selection.addRange(
        collapsed
      );

      selection.extend(
        focus.node,
        focus.offset
      );

      return true;
    }

    /*
     * Final compatibility fallback. Range itself has no backward
     * selection direction, so determine DOM order and restore the
     * same selected characters in forward order.
     */
    const anchorProbe =
      document.createRange();

    const focusProbe =
      document.createRange();

    anchorProbe.setStart(
      anchor.node,
      anchor.offset
    );

    anchorProbe.collapse(true);

    focusProbe.setStart(
      focus.node,
      focus.offset
    );

    focusProbe.collapse(true);

    const anchorFirst =
      anchorProbe.compareBoundaryPoints(
        window.Range.START_TO_START,
        focusProbe
      ) <= 0;

    const range =
      document.createRange();

    if (anchorFirst) {
      range.setStart(
        anchor.node,
        anchor.offset
      );

      range.setEnd(
        focus.node,
        focus.offset
      );
    } else {
      range.setStart(
        focus.node,
        focus.offset
      );

      range.setEnd(
        anchor.node,
        anchor.offset
      );
    }

    selection.addRange(range);

    return true;
  } catch {
    return false;
  }
}


function restoreSelection(element, startOffset, endOffset) {
  if (!element) {
    return;
  }

  const start = locateTextOffset(element, startOffset);
  const end = locateTextOffset(element, endOffset);
  const range = document.createRange();
  const selection = window.getSelection?.();

  try {
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    selection?.removeAllRanges();
    selection?.addRange(range);
  } catch {
    element.focus();
  }
}

function insertPlainTextAtSelection(element, text) {
  const selection = window.getSelection?.();

  if (!selection || selection.rangeCount < 1) {
    element.textContent += text;
    return;
  }

  const range = selection.getRangeAt(0);

  if (!element.contains(range.commonAncestorContainer)) {
    element.textContent += text;
    return;
  }

  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

function blockFontSize(block) {
  if (block.type !== "heading") {
    return 16;
  }

  if (block.level === 1) {
    return 28;
  }

  if (block.level === 2) {
    return 23;
  }

  return 19;
}

function listMarker(block, orderedIndex) {
  if (block.type !== "list_item") {
    return "";
  }

  return block.list?.type === "ordered"
    ? `${orderedIndex}.`
    : "•";
}

function blockLabel(block, index, isFR) {
  if (block.type === "heading") {
    return isFR
      ? `Titre ${block.level || 1}`
      : `Heading ${block.level || 1}`;
  }

  if (block.type === "blockquote") {
    return isFR ? "Citation" : "Blockquote";
  }

  if (block.type === "list_item") {
    return block.list?.type === "ordered"
      ? (isFR ? "Élément numéroté" : "Numbered item")
      : (isFR ? "Élément à puce" : "Bulleted item");
  }

  return isFR
    ? `Paragraphe ${index + 1}`
    : `Paragraph ${index + 1}`;
}

function EditorBlock({
  block,
  index,
  orderedIndex,
  active,
  isFR,
  registerRef,
  onActivate,
  onSelection,
  paragraphInFigureFlow = false,
  clearFigureFlow = false,
  compactContinuousParagraphSpacing = false,
  workingPageBreak = false,
  renderPageBreak = null,
}) {
  if (
    block.type === "page_break" &&
    typeof renderPageBreak ===
      "function"
  ) {
    const renderedBoundary =
      renderPageBreak(
        block
      );

    if (
      !renderedBoundary &&
      !workingPageBreak
    ) {
      return null;
    }

    return (
      <div
        contentEditable={false}
        suppressContentEditableWarning
        data-manuscript-embedded-boundary={
          block.id
        }
        data-manuscript-working-page-break={
          workingPageBreak
            ? "true"
            : undefined
        }
        data-manuscript-working-page-break-spacer-px={
          workingPageBreak
            ? "0"
            : undefined
        }
        style={{
          paddingTop: 0,
        }}
      >
        {renderedBoundary}
      </div>
    );
  }

  if (block.type === "page_break") {
    return (
      <div
        role="separator"
        aria-label={isFR ? "Saut de page" : "Page break"}
        style={{
          display: "flex",
          clear:
            clearFigureFlow
              ? "both"
              : undefined,
          alignItems: "center",
          gap: 10,
          color: "#64748b",
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        <span style={{ flex: 1, height: 1, background: "#cbd5e1" }} />
        {isFR ? "Saut de page" : "Page break"}
        <span style={{ flex: 1, height: 1, background: "#cbd5e1" }} />
      </div>
    );
  }

  const marker = listMarker(block, orderedIndex);
  const indentation = Number(block.indentation?.left) || 0;

  return (
    <div
      data-manuscript-active-block={active ? "true" : undefined}
      style={{
        display:
          paragraphInFigureFlow
            ? "block"
            : "grid",
        clear:
          clearFigureFlow
            ? "both"
            : undefined,
        gridTemplateColumns:
          paragraphInFigureFlow
            ? undefined
            : (
                marker
                  ? "28px minmax(0, 1fr)"
                  : "minmax(0, 1fr)"
              ),
        gap:
          paragraphInFigureFlow
            ? undefined
            : 8,
        marginLeft: Math.min(96, Math.round(indentation / 15)),
        padding:
          block.type === "blockquote"
            ? (
                compactContinuousParagraphSpacing
                  ? "7px 10px"
                  : "10px 12px"
              )
            : compactContinuousParagraphSpacing
              ? "0"
              : "4px 0",
        borderLeft: block.type === "blockquote" ? "4px solid #94a3b8" : "none",
        outline: "none",
        background: block.type === "blockquote" ? "#f8fafc" : "transparent",
        borderRadius: block.type === "blockquote" ? 8 : 0,
      }}
    >
      {marker ? (
        <span
          aria-hidden="true"
          style={{
            paddingTop: 8,
            fontWeight: 800,
            textAlign: "right",
            color: "#475569",
          }}
        >
          {marker}
        </span>
      ) : null}

      <div
        ref={(element) => registerRef(block.id, element)}
        role="textbox"
        aria-multiline="true"
        aria-label={blockLabel(block, index, isFR)}
        data-manuscript-block-id={block.id}
        onFocus={() => onActivate(block.id)}
        onClick={(event) => {
          onActivate(block.id);
          onSelection(block.id, event.currentTarget);
        }}
        onMouseUp={(event) => onSelection(block.id, event.currentTarget)}
        style={{
          minHeight:
            block.type === "heading"
              ? 42
              : compactContinuousParagraphSpacing
                ? 0
                : 34,
          padding:
            compactContinuousParagraphSpacing
              ? (
                  block.type === "heading"
                    ? "6px 0"
                    : "2px 0"
                )
              : "7px 9px",
          borderRadius: 8,
          outline: "none",
          background: "transparent",
          textAlign: block.alignment || "left",
          textJustify:
            block.alignment === "justify"
              ? "inter-word"
              : undefined,
          fontSize: blockFontSize(block),
          fontWeight: block.type === "heading" ? 800 : 500,
          lineHeight:
            manuscriptEditorPreviewLineHeight(
              block.lineSpacing
            ),
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {(Array.isArray(block.runs) && block.runs.length
          ? block.runs
          : [{ text: "" }]
        ).map((run, runIndex) => (
          <span
            key={`${block.id}-run-${runIndex}`}
            data-bold={Boolean(run.bold)}
            data-italic={Boolean(run.italic)}
            data-underline={Boolean(run.underline)}
            data-highlight={Boolean(run.highlight)}
            data-font-family={run.fontFamily || ""}
            data-font-size={run.fontSize || ""}
            style={{
              fontWeight: run.bold ? 800 : "inherit",
              fontStyle: run.italic ? "italic" : "normal",
              textDecoration: run.underline ? "underline" : "none",
              backgroundColor: run.highlight ? "#fef08a" : "transparent",
              fontFamily: run.fontFamily || "inherit",
              fontSize: run.fontSize ? `${run.fontSize}pt` : "inherit",
            }}
          >
            {run.text || ZERO_WIDTH}
          </span>
        ))}
      </div>
    </div>
  );
}


function ToolbarGroup({
  label,
  children,
  hidden = false,
}) {
  if (hidden) {
    return null;
  }

  return (
    <div
      className="ns-nbb-manuscript-toolbar-group"
      role="group"
      aria-label={label}
      style={{
        display: "grid",
        gap: 5,
        alignSelf: "stretch",
        paddingRight: 10,
        borderRight: "1px solid #e2e8f0",
      }}
    >
      <span
        className="ns-card-meta ns-nbb-manuscript-toolbar-label"
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function ChildrensBookManuscriptEditor({
  lang = "en",
  entry = {},
  disabled = false,
  selectionRestore = null,
  pageCanvasTrail = null,
  pageCanvasLabel = "",
  flowAnchorBlockId = "",
  flowContent = null,
  saveActionLabel = "",
  compactToolbar = false,
  enableHighlight = false,
  stickyToolbar = false,
  enableWholeDocumentJustify = false,
  embeddedFlow = false,
  compactContinuousParagraphSpacing = false,
  boundedSurface = false,
  workingPageGuideCount = 0,
  workingPageGuideLabel = "",
  workingPageBreakBoundaryIds = [],
  renderPageBreak = null,
  onChange,
}) {
  const isFR = String(lang).toLowerCase() === "fr";
  const resolvedSaveActionLabel =
    String(
      saveActionLabel || ""
    ).trim() ||
    (
      isFR
        ? "Enregistrer le Blueprint"
        : "Save Blueprint"
    );
  const state = useMemo(
    () => manuscriptEditorStateFromEntry(entry),
    [entry]
  );
  const entryContent = useMemo(
    () => state.ok
      ? editableManuscriptContent(state.content)
      : null,
    [state]
  );
  const entryIdentity = String(
    entry?.unitId || entry?.id || "manuscript-entry"
  );
  const entrySignature = useMemo(
    () => JSON.stringify(entryContent),
    [entryContent]
  );

  const incomingEntrySignature =
    useMemo(
      () =>
        JSON.stringify(
          entry
            ?.manuscriptContent ??
            entryContent
        ),
      [
        entry
          ?.manuscriptContent,
        entryContent,
      ]
    );

  const entryContentRef = useRef(entryContent);
  const emittedEntryKeys = useRef(new Set());
  const consumedSelectionRestoreKeys =
    useRef(
      new Set()
    );
  const [content, setContent] = useState(entryContent);
  const [activeBlockId, setActiveBlockId] = useState("");
  const [
    selectionRevision,
    setSelectionRevision,
  ] = useState(0);
  const refs = useRef(new Map());
  const surfaceRef = useRef(null);
  const pendingSelection = useRef(null);
  const pendingSurfaceSelection = useRef(null);
  const toolbarSelection = useRef(null);
  const formattingSelection = useRef(null);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [
    historyRevision,
    setHistoryRevision,
  ] = useState(0);

  const [
    workingPageGuideIntervalPx,
    setWorkingPageGuideIntervalPx,
  ] = useState(0);

  const workingPageBreakBoundarySet =
    useMemo(
      () =>
        new Set(
          (
            Array.isArray(
              workingPageBreakBoundaryIds
            )
              ? workingPageBreakBoundaryIds
              : []
          )
            .map(
              (value) =>
                String(
                  value || ""
                )
            )
            .filter(Boolean)
        ),
      [
        workingPageBreakBoundaryIds,
      ]
    );

  const workingPageBreakBoundaryKey =
    useMemo(
      () =>
        Array.from(
          workingPageBreakBoundarySet
        )
          .sort()
          .join("::"),
      [
        workingPageBreakBoundarySet,
      ]
    );

  const selectionRestoreKey =
    useMemo(
      () =>
        JSON.stringify({
          unitId:
            String(
              selectionRestore
                ?.unitId ||
                ""
            ),

          expectedEntrySignature:
            String(
              selectionRestore
                ?.expectedEntrySignature ||
                ""
            ),

          revision:
            Number(
              selectionRestore
                ?.revision
            ) || 0,

          selection: {
            blockId:
              String(
                selectionRestore
                  ?.selection
                  ?.blockId ||
                  ""
              ),

            start:
              Math.max(
                0,
                Number(
                  selectionRestore
                    ?.selection
                    ?.start
                ) || 0
              ),

            end:
              Math.max(
                0,
                Number(
                  selectionRestore
                    ?.selection
                    ?.end
                ) || 0
              ),
          },
        }),
      [
        selectionRestore,
      ]
    );

  entryContentRef.current = entryContent;

  useEffect(() => {
    const requestedCount =
      Math.max(
        1,
        Number(
          workingPageGuideCount
        ) || 1
      );

    if (
      !boundedSurface ||
      requestedCount <= 1
    ) {
      setWorkingPageGuideIntervalPx(0);
      return undefined;
    }

    const surface = surfaceRef.current;
    if (!surface) {
      return undefined;
    }

    let frame = null;
    let resizeObserver = null;

    const updateGuideInterval = () => {
      const workingPageBreakSpacerTotal =
        Array.from(
          surface.querySelectorAll?.(
            '[data-manuscript-working-page-break="true"]'
          ) || []
        ).reduce(
          (
            total,
            element
          ) =>
            total +
            Math.max(
              0,
              Number(
                element
                  ?.dataset
                  ?.manuscriptWorkingPageBreakSpacerPx ||
                0
              ) || 0
            ),
          0
        );

      const naturalScrollHeight =
        Math.max(
          0,
          Number(
            surface.scrollHeight ||
            0
          ) -
          workingPageBreakSpacerTotal
        );

      const totalHeight =
        Math.max(
          naturalScrollHeight,
          Number(surface.clientHeight || 0)
        );
      const nextInterval =
        totalHeight > 0
          ? totalHeight / requestedCount
          : 0;

      setWorkingPageGuideIntervalPx(
        (current) =>
          Math.abs(current - nextInterval) < 0.5
            ? current
            : nextInterval
      );
    };

    const scheduleUpdate = () => {
      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        if (
          frame != null &&
          typeof window.cancelAnimationFrame === "function"
        ) {
          window.cancelAnimationFrame(frame);
        }
        frame = window.requestAnimationFrame(updateGuideInterval);
      } else {
        updateGuideInterval();
      }
    };

    if (
      typeof window !== "undefined" &&
      typeof window.ResizeObserver === "function"
    ) {
      resizeObserver = new window.ResizeObserver(scheduleUpdate);
      resizeObserver.observe(surface);
    }

    scheduleUpdate();

    return () => {
      resizeObserver?.disconnect?.();
      if (
        frame != null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [
    boundedSurface,
    content,
    workingPageBreakBoundaryKey,
    workingPageGuideCount,
  ]);

  useLayoutEffect(() => {
    const surface =
      surfaceRef.current;

    if (
      !boundedSurface ||
      !surface ||
      !Number.isFinite(
        workingPageGuideIntervalPx
      ) ||
      workingPageGuideIntervalPx <=
        0
    ) {
      return undefined;
    }

    const boundaries =
      Array.from(
        surface.querySelectorAll?.(
          '[data-manuscript-working-page-break="true"]'
        ) || []
      );

    if (!boundaries.length) {
      return undefined;
    }

    boundaries.forEach(
      (element) => {
        element.style.paddingTop =
          "0px";

        element.dataset
          .manuscriptWorkingPageBreakSpacerPx =
          "0";
      }
    );

    const epsilon = 2;

    boundaries.forEach(
      (element) => {
        const boundaryTop =
          Math.max(
            0,
            Number(
              element.offsetTop ||
              0
            )
          );

        const rawRemainder =
          boundaryTop %
          workingPageGuideIntervalPx;

        const remainder =
          rawRemainder < 0
            ? rawRemainder +
              workingPageGuideIntervalPx
            : rawRemainder;

        const distanceToNext =
          workingPageGuideIntervalPx -
          remainder;

        const spacer =
          (
            remainder <= epsilon ||
            distanceToNext <=
              epsilon
          )
            ? 0
            : distanceToNext;

        element.style.paddingTop =
          `${spacer}px`;

        element.dataset
          .manuscriptWorkingPageBreakSpacerPx =
          String(spacer);
      }
    );

    return () => {
      boundaries.forEach(
        (element) => {
          element.style.paddingTop =
            "0px";

          element.dataset
            .manuscriptWorkingPageBreakSpacerPx =
            "0";
        }
      );
    };
  }, [
    boundedSurface,
    content,
    workingPageBreakBoundaryKey,
    workingPageGuideIntervalPx,
  ]);

  useEffect(() => {
    const externalKey =
      `${entryIdentity}::${entrySignature}`;

    const requestedUnitId =
      String(
        selectionRestore
          ?.unitId ||
          ""
      );

    const requestedBlockId =
      String(
        selectionRestore
          ?.selection
          ?.blockId ||
          ""
      );

    const expectedEntrySignature =
      String(
        selectionRestore
          ?.expectedEntrySignature ||
          ""
      );

    const restoreMatches =
      requestedUnitId ===
        entryIdentity &&
      Boolean(
        requestedBlockId
      ) &&
      (
        !expectedEntrySignature ||
        expectedEntrySignature ===
          incomingEntrySignature
      ) &&
      !consumedSelectionRestoreKeys
        .current
        .has(
          selectionRestoreKey
        );

    const requestedSelection =
      restoreMatches
        ? {
            blockId:
              requestedBlockId,

            start:
              Math.max(
                0,
                Number(
                  selectionRestore
                    ?.selection
                    ?.start
                ) || 0
              ),

            end:
              Math.max(
                0,
                Number(
                  selectionRestore
                    ?.selection
                    ?.end
                ) || 0
              ),
          }
        : null;

    if (
      requestedSelection
    ) {
      consumedSelectionRestoreKeys
        .current
        .add(
          selectionRestoreKey
        );
    }

    if (
      emittedEntryKeys
        .current
        .has(
          externalKey
        )
    ) {
      emittedEntryKeys
        .current
        .delete(
          externalKey
        );

      if (
        requestedSelection
      ) {
        pendingSelection.current =
          requestedSelection;

        toolbarSelection.current =
          requestedSelection;

        formattingSelection.current = {
          anchorBlockId:
            requestedSelection
              .blockId,
          anchorOffset:
            requestedSelection
              .start,
          focusBlockId:
            requestedSelection
              .blockId,
          focusOffset:
            requestedSelection
              .end,
        };

        setActiveBlockId(
          requestedSelection
            .blockId
        );

        setSelectionRevision(
          (revision) =>
            revision + 1
        );
      }

      return;
    }

    setContent(
      entryContentRef.current
    );

    undoStack.current = [];
    redoStack.current = [];
    setHistoryRevision(
      (revision) =>
        revision + 1
    );

    pendingSurfaceSelection.current =
      null;

    if (
      requestedSelection
    ) {
      setActiveBlockId(
        requestedSelection
          .blockId
      );

      pendingSelection.current =
        requestedSelection;

      toolbarSelection.current =
        requestedSelection;

      formattingSelection.current = {
        anchorBlockId:
          requestedSelection
            .blockId,
        anchorOffset:
          requestedSelection
            .start,
        focusBlockId:
          requestedSelection
            .blockId,
        focusOffset:
          requestedSelection
            .end,
      };

      setSelectionRevision(
        (revision) =>
          revision + 1
      );

      return;
    }

    setActiveBlockId("");
    pendingSelection.current = null;
    toolbarSelection.current = null;
    formattingSelection.current = null;
  }, [
    entryIdentity,
    entrySignature,
    incomingEntrySignature,
    selectionRestore,
    selectionRestoreKey,
  ]);

  const editableBlocks = useMemo(
    () => content?.blocks || [],
    [content]
  );
  const activeBlock = editableBlocks.find(
    (block) => block.id === activeBlockId
  ) || editableBlocks.find(
    (block) => block.type !== "page_break"
  );

  useEffect(() => {
    const activeStillExists =
      editableBlocks.some(
        (block) =>
          block.id === activeBlockId &&
          block.type !== "page_break"
      );

    if (!activeStillExists && activeBlock?.id) {
      setActiveBlockId(activeBlock.id);
    }
  }, [
    activeBlock?.id,
    activeBlockId,
    editableBlocks,
  ]);

  useLayoutEffect(() => {
    const pendingSurface =
      pendingSurfaceSelection.current;

    if (pendingSurface) {
      restoreManuscriptSurfaceDomSelection(
        surfaceRef.current,
        pendingSurface
      );

      pendingSurfaceSelection.current =
        null;

      return;
    }

    const pending =
      pendingSelection.current;

    if (!pending) {
      return;
    }

    const element =
      refs.current.get(
        pending.blockId
      );

    if (!element) {
      return;
    }

    surfaceRef.current?.focus();

    restoreSelection(
      element,
      pending.start,
      pending.end
    );

    pendingSelection.current =
      null;
  }, [
    content,
    selectionRevision,
  ]);

  function registerRef(blockId, element) {
    if (element) {
      refs.current.set(blockId, element);
    } else {
      refs.current.delete(blockId);
    }
  }

  function emit(
    nextContent,
    selection,
    editIntent = null,
    historyOptions = {}
  ) {
    if (!nextContent || typeof onChange !== "function") {
      return;
    }

    const fields = manuscriptEntryFieldsFromEditorContent(nextContent);

    if (!fields.ok) {
      return;
    }

    const recordHistory =
      historyOptions
        ?.recordHistory !==
        false;

    if (
      recordHistory &&
      JSON.stringify(
        nextContent
      ) !==
        JSON.stringify(
          content
        )
    ) {
      const previousSelection =
        toolbarSelection.current
          ? {
              ...toolbarSelection.current,
            }
          : activeBlock?.id
            ? {
                blockId:
                  activeBlock.id,
                start: 0,
                end: 0,
              }
            : null;

      undoStack.current.push({
        content,
        selection:
          previousSelection,
      });

      if (
        undoStack.current.length >
          100
      ) {
        undoStack.current.shift();
      }

      redoStack.current = [];

      setHistoryRevision(
        (revision) =>
          revision + 1
      );
    }

    if (selection) {
      pendingSelection.current =
        selection;

      toolbarSelection.current =
        selection;

      formattingSelection.current = {
        anchorBlockId:
          selection.blockId,
        anchorOffset:
          selection.start,
        focusBlockId:
          selection.blockId,
        focusOffset:
          selection.end,
      };

      setActiveBlockId(
        selection.blockId
      );
    }

    const nextContentValue = fields.manuscriptContent;
    const externalKey = `${entryIdentity}::${JSON.stringify(nextContentValue)}`;

    emittedEntryKeys.current.add(externalKey);
    setContent(nextContentValue);

    const selectionHandoff =
      selection?.blockId
        ? {
            blockId:
              String(
                selection.blockId
              ),
            start:
              Math.max(
                0,
                Number(
                  selection.start
                ) || 0
              ),
            end:
              Math.max(
                0,
                Number(
                  selection.end
                ) || 0
              ),
          }
        : null;

    onChange({
      manuscriptContent:
        nextContentValue,
      manuscriptText:
        fields.manuscriptText,

      ...(selectionHandoff
        ? {
            selectionHandoff,
          }
        : {}),

      ...(editIntent
        ? {
            editIntent,
          }
        : {}),
    });
  }

  function emitSurfaceFormatting(
    nextContent,
    selection
  ) {
    if (
      !nextContent ||
      !selection ||
      typeof onChange !== "function"
    ) {
      return;
    }

    const range =
      normalizeManuscriptEditorSurfaceSelection(
        nextContent,
        selection
      );

    if (
      !range ||
      range.crossesPageBreak
    ) {
      return;
    }

    pendingSurfaceSelection.current =
      selection;

    formattingSelection.current =
      selection;

    if (
      range.startBlockId ===
      range.endBlockId
    ) {
      toolbarSelection.current = {
        blockId:
          range.startBlockId,
        start:
          range.startOffset,
        end:
          range.endOffset,
      };
    } else {
      toolbarSelection.current =
        null;
    }

    setActiveBlockId(
      selection.focusBlockId ||
        range.endBlockId
    );

    emit(nextContent);
  }


  function liveSurfaceSelection() {
    const domSelection =
      manuscriptSurfaceDomSelection(
        surfaceRef.current
      );

    if (!domSelection) {
      return null;
    }

    const range =
      normalizeManuscriptEditorSurfaceSelection(
        content,
        domSelection
      );

    if (!range) {
      return null;
    }

    return {
      domSelection,
      range,
    };
  }

  function isCrossBlockSelection(live) {
    return Boolean(
      live?.range &&
      live.range.startBlockId !==
        live.range.endBlockId
    );
  }

  function replaceLiveSurfaceSelection(
    live,
    text
  ) {
    if (!live) {
      return false;
    }

    const result =
      replaceManuscriptEditorSurfaceSelection(
        content,
        live.domSelection,
        text
      );

    if (!result?.content) {
      return false;
    }

    emit(
      result.content,
      result.selection
    );

    return true;
  }

  function rememberLiveSurfaceSelection() {
    const live =
      liveSurfaceSelection();

    if (!live) {
      return;
    }

    const focusBlockId =
      live.domSelection.focusBlockId;

    if (focusBlockId) {
      setActiveBlockId(
        focusBlockId
      );
    }

    if (
      live.range.crossesPageBreak
    ) {
      formattingSelection.current =
        null;
    } else {
      formattingSelection.current =
        live.domSelection;
    }

    if (
      live.range.startBlockId ===
      live.range.endBlockId
    ) {
      toolbarSelection.current = {
        blockId:
          live.range.startBlockId,
        start:
          live.range.startOffset,
        end:
          live.range.endOffset,
      };
    } else {
      /*
       * Paragraph-level controls remain paragraph-scoped.
       * Cross-block authority is retained separately for
       * inline formatting only.
       */
      toolbarSelection.current =
        null;
    }

    setSelectionRevision(
      (revision) =>
        revision + 1
    );
  }

  function synchronizedContent(blockId) {
    const element = refs.current.get(blockId);

    if (!element || !content) {
      return content;
    }

    return replaceManuscriptEditorBlockRuns(
      content,
      blockId,
      runsFromEditorElement(element)
    );
  }

  function synchronizedFormattingContent(
    selection
  ) {
    const range =
      normalizeManuscriptEditorSurfaceSelection(
        content,
        selection
      );

    if (
      !range ||
      range.crossesPageBreak
    ) {
      return null;
    }

    let nextContent =
      content;

    for (
      let index =
        range.startBlockIndex;
      index <=
        range.endBlockIndex;
      index += 1
    ) {
      const block =
        nextContent?.blocks?.[
          index
        ];

      if (
        !block ||
        block.type ===
          "page_break"
      ) {
        return null;
      }

      const element =
        refs.current.get(
          block.id
        );

      if (!element) {
        continue;
      }

      nextContent =
        replaceManuscriptEditorBlockRuns(
          nextContent,
          block.id,
          runsFromEditorElement(
            element
          )
        );
    }

    return nextContent;
  }


  function handleInput(blockId, event) {
    const offsets = selectionOffsets(event.currentTarget);
    const nextContent = replaceManuscriptEditorBlockRuns(
      content,
      blockId,
      runsFromEditorElement(event.currentTarget)
    );

    emit(nextContent, {
      blockId,
      ...offsets,
    });
  }

  function handleKeyDown(blockId, event) {
    if (disabled) {
      return;
    }

    const element = refs.current.get(blockId);
    let offsets = selectionOffsets(element);

    if (event.key === "Enter" && event.shiftKey) {
      event.preventDefault();
      insertPlainTextAtSelection(element, "\n");
      offsets = selectionOffsets(element);

      const nextContent = replaceManuscriptEditorBlockRuns(
        content,
        blockId,
        runsFromEditorElement(element)
      );

      emit(nextContent, {
        blockId,
        ...offsets,
      });
      return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();

      if (offsets.start !== offsets.end) {
        insertPlainTextAtSelection(element, "");
        offsets = selectionOffsets(element);
      }

      const synced = replaceManuscriptEditorBlockRuns(
        content,
        blockId,
        runsFromEditorElement(element)
      );
      const result = splitManuscriptEditorBlock(
        synced,
        blockId,
        offsets.start
      );

      if (result?.content) {
        emit(
          result.content,
          {
            blockId:
              result.nextBlockId,
            start: 0,
            end: 0,
          },
          {
            type:
              "paragraph_break",

            sourceBlockId:
              blockId,

            sourceOffset:
              offsets.start,

            continuationBlockId:
              result.nextBlockId,
          }
        );
      }
      return;
    }

    if (
      event.key === "Backspace" &&
      offsets.start === 0 &&
      offsets.end === 0
    ) {
      const synced = synchronizedContent(blockId);
      const result = mergeManuscriptEditorBlockWithPrevious(
        synced,
        blockId
      );

      if (
        result?.content &&
        result.targetBlockId !== blockId
      ) {
        event.preventDefault();
        emit(result.content, {
          blockId: result.targetBlockId,
          start: result.targetOffset,
          end: result.targetOffset,
        });
      }
    }
  }

  function handlePaste(blockId, event) {
    if (disabled) {
      return;
    }

    event.preventDefault();
    const element = refs.current.get(blockId);

    if (!element) {
      return;
    }

    const offsets = selectionOffsets(element);
    const text = cleanEditorText(
      event.clipboardData?.getData("text/plain")
    );
    const synced = replaceManuscriptEditorBlockRuns(
      content,
      blockId,
      runsFromEditorElement(element)
    );
    const result = insertManuscriptEditorPlainTextAtSelection(
      synced,
      blockId,
      offsets.start,
      offsets.end,
      text
    );

    if (result?.content) {
      emit(result.content, result.selection);
    }
  }

  function handleSurfaceBeforeInput(event) {
    if (disabled) {
      event.preventDefault();
      return;
    }

    const live =
      liveSurfaceSelection();

    if (
      !live ||
      !isCrossBlockSelection(live)
    ) {
      return;
    }

    /*
     * Never allow the browser to invent its own DOM merge across
     * authoritative manuscript blocks. Cross-block mutations are
     * translated into the deterministic manuscript model instead.
     */
    event.preventDefault();

    const nativeEvent =
      event.nativeEvent || {};

    const inputType =
      String(
        nativeEvent.inputType ||
        ""
      );

    const data =
      nativeEvent.data ??
      event.data ??
      "";

    if (
      inputType ===
        "insertParagraph"
    ) {
      replaceLiveSurfaceSelection(
        live,
        "\n\n"
      );
      return;
    }

    if (
      inputType ===
        "insertLineBreak"
    ) {
      replaceLiveSurfaceSelection(
        live,
        "\n"
      );
      return;
    }

    if (
      inputType.startsWith(
        "delete"
      )
    ) {
      replaceLiveSurfaceSelection(
        live,
        ""
      );
      return;
    }

    if (
      inputType.startsWith(
        "insert"
      ) ||
      (
        !inputType &&
        data !== null &&
        data !== undefined
      )
    ) {
      replaceLiveSurfaceSelection(
        live,
        String(data || "")
      );
    }
  }

  function handleSurfaceInput() {
    const live =
      liveSurfaceSelection();

    if (
      !live ||
      isCrossBlockSelection(live)
    ) {
      return;
    }

    const blockId =
      live.domSelection.focusBlockId;

    const element =
      refs.current.get(
        blockId
      );

    if (!element) {
      return;
    }

    handleInput(
      blockId,
      {
        currentTarget:
          element,
      }
    );
  }

  function handleSurfaceKeyDown(event) {
    if (disabled) {
      return;
    }

    if (
      (
        event.metaKey ||
        event.ctrlKey
      ) &&
      String(
        event.key || ""
      ).toLowerCase() ===
        "z"
    ) {
      event.preventDefault();

      if (event.shiftKey) {
        redoEditorChange();
      } else {
        undoEditorChange();
      }

      return;
    }

    const live =
      liveSurfaceSelection();

    if (!live) {
      return;
    }

    const blockId =
      live.domSelection.focusBlockId;

    if (blockId) {
      setActiveBlockId(
        blockId
      );
    }

    if (
      isCrossBlockSelection(
        live
      )
    ) {
      if (
        event.key === "Enter"
      ) {
        event.preventDefault();

        replaceLiveSurfaceSelection(
          live,
          event.shiftKey
            ? "\n"
            : "\n\n"
        );

        return;
      }

      if (
        event.key === "Backspace" ||
        event.key === "Delete"
      ) {
        event.preventDefault();

        replaceLiveSurfaceSelection(
          live,
          ""
        );

        return;
      }

      /*
       * Printable text is handled by beforeinput so IME/text
       * insertion does not depend on keyboard layout.
       */
      return;
    }

    handleKeyDown(
      blockId,
      event
    );
  }

  function handleSurfacePaste(event) {
    if (disabled) {
      return;
    }

    const live =
      liveSurfaceSelection();

    if (!live) {
      return;
    }

    if (
      isCrossBlockSelection(
        live
      )
    ) {
      event.preventDefault();

      const text =
        cleanEditorText(
          event.clipboardData
            ?.getData(
              "text/plain"
            )
        );

      replaceLiveSurfaceSelection(
        live,
        text
      );

      return;
    }

    handlePaste(
      live.domSelection
        .focusBlockId,
      event
    );
  }

  function activeFormattingSelection() {
    const live =
      liveSurfaceSelection();

    if (
      live?.range &&
      !live.range.crossesPageBreak
    ) {
      formattingSelection.current =
        live.domSelection;

      return live.domSelection;
    }

    const remembered =
      formattingSelection.current;

    if (remembered) {
      const range =
        normalizeManuscriptEditorSurfaceSelection(
          content,
          remembered
        );

      if (
        range &&
        !range.crossesPageBreak
      ) {
        return remembered;
      }
    }

    const paragraphSelection =
      activeSelection();

    if (!paragraphSelection) {
      return null;
    }

    const selection = {
      anchorBlockId:
        paragraphSelection.blockId,
      anchorOffset:
        paragraphSelection.start,
      focusBlockId:
        paragraphSelection.blockId,
      focusOffset:
        paragraphSelection.end,
    };

    formattingSelection.current =
      selection;

    return selection;
  }


  function activeSelection() {
    const live =
      liveSurfaceSelection();

    if (live?.range) {
      if (
        live.range.startBlockId !==
        live.range.endBlockId
      ) {
        return null;
      }

      /*
       * A browser selection left behind in a previously active
       * paragraph must not override an explicitly activated
       * paragraph. This preserves paragraph-control authority
       * after the move to one shared editing host.
       */
      if (
        !activeBlock ||
        live.range.startBlockId ===
          activeBlock.id
      ) {
        const selection = {
          blockId:
            live.range.startBlockId,
          start:
            live.range.startOffset,
          end:
            live.range.endOffset,
        };

        toolbarSelection.current =
          selection;

        return selection;
      }
    }

    if (!activeBlock) {
      return null;
    }

    const element = refs.current.get(activeBlock.id);
    const blockSelection = selectedOffsets(element);

    if (blockSelection) {
      const selection = {
        blockId: activeBlock.id,
        ...blockSelection,
      };

      toolbarSelection.current = selection;
      return selection;
    }

    if (
      toolbarSelection.current?.blockId ===
      activeBlock.id
    ) {
      return toolbarSelection.current;
    }

    const offsets = selectionOffsets(element);

    return {
      blockId: activeBlock.id,
      ...offsets,
    };
  }

  function paragraphCommandSelection() {
    const remembered =
      toolbarSelection.current;

    if (
      remembered?.blockId
    ) {
      const exists =
        content?.blocks?.some(
          (block) =>
            block?.id ===
              remembered.blockId &&
            block?.type !==
              "page_break"
        );

      if (exists) {
        return {
          ...remembered,
        };
      }
    }

    return activeSelection();
  }


  function rememberSelection(blockId, element) {
    const offsets = selectionOffsets(element);

    toolbarSelection.current = {
      blockId,
      ...offsets,
    };

    formattingSelection.current = {
      anchorBlockId:
        blockId,
      anchorOffset:
        offsets.start,
      focusBlockId:
        blockId,
      focusOffset:
        offsets.end,
    };

    setSelectionRevision(
      (revision) =>
        revision + 1
    );
  }

  function restoreHistorySnapshot(
    snapshot,
    destinationStack
  ) {
    if (
      !snapshot?.content
    ) {
      return;
    }

    destinationStack.current.push({
      content,
      selection:
        toolbarSelection.current
          ? {
              ...toolbarSelection.current,
            }
          : null,
    });

    emit(
      snapshot.content,
      snapshot.selection || null,
      {
        type:
          "history_restore",
      },
      {
        recordHistory: false,
      }
    );

    setHistoryRevision(
      (revision) =>
        revision + 1
    );
  }


  function undoEditorChange() {
    const snapshot =
      undoStack.current.pop();

    if (!snapshot) {
      return;
    }

    restoreHistorySnapshot(
      snapshot,
      redoStack
    );
  }


  function redoEditorChange() {
    const snapshot =
      redoStack.current.pop();

    if (!snapshot) {
      return;
    }

    restoreHistorySnapshot(
      snapshot,
      undoStack
    );
  }


  function applyInlineMark(mark) {
    const selection =
      activeFormattingSelection();

    if (!selection) {
      return;
    }

    const synced =
      synchronizedFormattingContent(
        selection
      );

    if (!synced) {
      return;
    }

    const nextContent =
      toggleManuscriptEditorSurfaceInlineMark(
        synced,
        selection,
        mark
      );

    if (!nextContent) {
      return;
    }

    emitSurfaceFormatting(
      nextContent,
      selection
    );
  }

  function applyTypography(
    property,
    value
  ) {
    const selection =
      activeFormattingSelection();

    if (!selection) {
      return;
    }

    const synced =
      synchronizedFormattingContent(
        selection
      );

    if (!synced) {
      return;
    }

    const nextContent =
      setManuscriptEditorSurfaceTypography(
        synced,
        selection,
        property,
        value
      );

    if (!nextContent) {
      return;
    }

    emitSurfaceFormatting(
      nextContent,
      selection
    );
  }

  function adjustFontSize(direction) {
    const selection =
      activeFormattingSelection();

    if (!selection) {
      return;
    }

    const range =
      normalizeManuscriptEditorSurfaceSelection(
        content,
        selection
      );

    if (
      !range ||
      range.crossesPageBreak
    ) {
      return;
    }

    const startBlock =
      editableBlocks.find(
        (block) =>
          block.id ===
          range.startBlockId
      );

    if (!startBlock) {
      return;
    }

    const typography =
      manuscriptEditorTypographyAtOffset(
        content,
        range.startBlockId,
        range.startOffset
      );

    const current =
      typography.fontSize ||
      defaultFontSizeForBlock(
        startBlock
      );

    applyTypography(
      "fontSize",
      nextFontSize(
        current,
        direction
      )
    );
  }

  function toggleList(type) {
    const selection =
      paragraphCommandSelection();

    if (!selection) {
      return;
    }

    const targetBlock =
      content?.blocks?.find(
        (block) =>
          block?.id ===
            selection.blockId
      );

    if (
      !targetBlock ||
      targetBlock.type ===
        "page_break"
    ) {
      return;
    }

    const current =
      manuscriptEditorBlockPresentation(
        targetBlock
      );

    const target =
      current === type
        ? "paragraph"
        : type;

    applyPresentation(
      target,
      selection
    );
  }

  function applyPresentation(
    presentation,
    lockedSelection = null
  ) {
    const selection =
      lockedSelection ||
      paragraphCommandSelection();

    if (!selection) {
      return;
    }

    const synced = synchronizedContent(selection.blockId);
    const result = setManuscriptEditorSelectedParagraphPresentation(
      synced,
      selection.blockId,
      selection.start,
      selection.end,
      presentation
    );

    if (result?.content) {
      emit(result.content, result.selection);
    }
  }

  function applyAlignment(alignment) {
    const selection =
      paragraphCommandSelection();

    if (!selection) {
      return;
    }

    const synced = synchronizedContent(selection.blockId);
    const result = setManuscriptEditorSelectedParagraphAlignment(
      synced,
      selection.blockId,
      selection.start,
      selection.end,
      alignment
    );

    if (result?.content) {
      emit(result.content, result.selection);
    }
  }

  function synchronizedWholeDocumentContent() {
    if (!content) {
      return null;
    }

    let nextContent =
      content;

    (content.blocks || [])
      .forEach(
        (block) => {
          if (
            !block ||
            block.type ===
              "page_break"
          ) {
            return;
          }

          const element =
            refs.current.get(
              block.id
            );

          if (!element) {
            return;
          }

          nextContent =
            replaceManuscriptEditorBlockRuns(
              nextContent,
              block.id,
              runsFromEditorElement(
                element
              )
            ) ||
            nextContent;
        }
      );

    return nextContent;
  }

  function justifyWholeDocument() {
    if (
      !enableWholeDocumentJustify ||
      !content
    ) {
      return;
    }

    const synced =
      synchronizedWholeDocumentContent();

    const nextContent =
      setManuscriptEditorWholeDocumentAlignment(
        synced,
        "justify"
      );

    if (!nextContent) {
      return;
    }

    emit(
      nextContent,
      null,
      {
        type:
          "justify_whole_document",
      }
    );
  }


  function applyLineSpacing(
    requestedLineSpacing
  ) {
    const selection =
      paragraphCommandSelection();

    if (!selection) {
      return;
    }

    const synced =
      synchronizedContent(
        selection.blockId
      );

    const result =
      setManuscriptEditorSelectedParagraphLineSpacing(
        synced,
        selection.blockId,
        selection.start,
        selection.end,
        requestedLineSpacing
      );

    if (result?.content) {
      emit(
        result.content,
        result.selection
      );
    }
  }


  function applyIndent(direction) {
    const selection =
      paragraphCommandSelection();

    if (!selection) {
      return;
    }

    const synced = synchronizedContent(selection.blockId);
    const result = changeManuscriptEditorSelectedParagraphIndent(
      synced,
      selection.blockId,
      selection.start,
      selection.end,
      direction
    );

    if (result?.content) {
      emit(result.content, result.selection);
    }
  }

  const canUndo =
    historyRevision >= 0 &&
    undoStack.current.length > 0;

  const canRedo =
    historyRevision >= 0 &&
    redoStack.current.length > 0;

  if (!state.ok || !content) {
    return (
      <div
        role="alert"
        style={{
          padding: 14,
          borderRadius: 10,
          border: "1px solid #fecaca",
          background: "#fef2f2",
          color: "#991b1b",
          lineHeight: 1.55,
        }}
      >
        <strong>
          {isFR
            ? "La mise en forme ne peut pas être modifiée en toute sécurité."
            : "Formatting cannot be edited safely."}
        </strong>
        <div style={{ marginTop: 5 }}>
          {isFR
            ? "Le contenu structuré de cette unité est invalide. Aucun texte de secours n’a été substitué."
            : "This unit’s structured manuscript content is invalid. No fallback text has been substituted."}
        </div>
      </div>
    );
  }

  let orderedIndex = 0;
  let figureFlowOpen = false;

  const requestedFlowAnchor =
    String(
      flowAnchorBlockId || ""
    ).trim();

  const flowEnabled =
    Boolean(
      flowContent &&
      requestedFlowAnchor &&
      editableBlocks.some(
        (block) =>
          block.type ===
            "paragraph" &&
          String(
            block.id || ""
          ) ===
            requestedFlowAnchor
      )
    );


  const typographyOffset =
    toolbarSelection.current?.blockId === activeBlock?.id
      ? toolbarSelection.current.start
      : 0;
  const activeTypography = activeBlock
    ? manuscriptEditorTypographyAtOffset(
        content,
        activeBlock.id,
        typographyOffset
      )
    : { fontFamily: "", fontSize: 0 };

  const wholeDocumentBodyBlocks =
    (content?.blocks || [])
      .filter(
        (block) =>
          [
            "paragraph",
            "blockquote",
            "list_item",
          ].includes(
            block?.type
          )
      );

  const wholeDocumentBodyJustified =
    wholeDocumentBodyBlocks.length > 0 &&
    wholeDocumentBodyBlocks.every(
      (block) =>
        block.alignment ===
          "justify"
    );

  return (
    <div
      className="ns-nbb-manuscript-formatting-editor"
      style={{ display: "grid", gap: 12 }}
    >
      <div
        className="ns-nbb-manuscript-formatting-toolbar"
        data-manuscript-toolbar-mode={
          compactToolbar
            ? "compact"
            : "full"
        }
        role="toolbar"
        aria-label={isFR ? "Mise en forme du manuscrit" : "Manuscript formatting"}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          flexWrap: "wrap",
          padding: 12,
          borderRadius: 12,
          border: "1px solid #cbd5e1",
          background: "#f8fafc",
          ...(stickyToolbar
            ? {
                position: "sticky",
                top: 0,
                zIndex: 20,
                boxShadow:
                  "0 6px 18px rgba(15,23,42,0.10)",
              }
            : {}),
        }}
        onMouseDown={(event) => {
          const inlineSelection =
            activeFormattingSelection();

          if (inlineSelection) {
            formattingSelection.current =
              inlineSelection;
          }

          toolbarSelection.current =
            activeSelection();

          if (
            event.target.closest(
              "button"
            )
          ) {
            event.preventDefault();
          }
        }}
      >
        <ToolbarGroup
          label={isFR ? "Historique" : "History"}
        >
          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={
              disabled ||
              !canUndo
            }
            onClick={
              undoEditorChange
            }
            title={
              isFR
                ? "Annuler la dernière modification"
                : "Undo last change"
            }
            aria-label={
              isFR
                ? "Annuler la dernière modification"
                : "Undo last change"
            }
          >
            {isFR
              ? "Annuler"
              : "Undo"}
          </button>

          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={
              disabled ||
              !canRedo
            }
            onClick={
              redoEditorChange
            }
            title={
              isFR
                ? "Rétablir la modification"
                : "Redo change"
            }
            aria-label={
              isFR
                ? "Rétablir la modification"
                : "Redo change"
            }
          >
            {isFR
              ? "Rétablir"
              : "Redo"}
          </button>
        </ToolbarGroup>

        <ToolbarGroup
          label={isFR ? "Structure" : "Structure"}
          hidden={compactToolbar}
        >
          <select
            aria-label={isFR ? "Style du bloc" : "Block style"}
            disabled={disabled || !activeBlock}
            value={manuscriptEditorBlockPresentation(activeBlock)}
            onChange={(event) => applyPresentation(event.target.value)}
            style={{
              minHeight: 36,
              padding: "6px 9px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#fff",
            }}
          >
            <option value="paragraph">{isFR ? "Paragraphe" : "Paragraph"}</option>
            <option value="heading-1">{isFR ? "Titre 1" : "Heading 1"}</option>
            <option value="heading-2">{isFR ? "Titre 2" : "Heading 2"}</option>
            <option value="heading-3">{isFR ? "Titre 3" : "Heading 3"}</option>
            <option value="blockquote">{isFR ? "Citation" : "Blockquote"}</option>
          </select>
        </ToolbarGroup>

        <ToolbarGroup
          label={isFR ? "Police" : "Font"}
          hidden={compactToolbar}
        >
          <select
            aria-label={isFR ? "Famille de police" : "Font family"}
            disabled={disabled || !activeBlock}
            value={activeTypography.fontFamily}
            onChange={(event) =>
              applyTypography("fontFamily", event.target.value || undefined)
            }
            style={{
              minHeight: 36,
              minWidth: 150,
              padding: "6px 9px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#fff",
            }}
          >
            <option value="">{isFR ? "Police du livre" : "Book font"}</option>
            {MANUSCRIPT_FONT_FAMILIES.map((family) => (
              <option key={family} value={family}>{family}</option>
            ))}
          </select>

          <select
            aria-label={isFR ? "Taille du texte" : "Text size"}
            disabled={disabled || !activeBlock}
            value={activeTypography.fontSize || ""}
            onChange={(event) =>
              applyTypography("fontSize", event.target.value || undefined)
            }
            style={{
              minHeight: 36,
              minWidth: 86,
              padding: "6px 9px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#fff",
            }}
          >
            <option value="">{isFR ? "Par défaut" : "Default"}</option>
            {FONT_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size} pt</option>
            ))}
          </select>

          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={disabled || !activeBlock}
            onClick={() => adjustFontSize("decrease")}
            title={isFR ? "Réduire la taille du texte" : "Decrease text size"}
            aria-label={isFR ? "Réduire la taille du texte" : "Decrease text size"}
            style={{ minHeight: 36, padding: "6px 10px", fontWeight: 900 }}
          >
            A−
          </button>
          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={disabled || !activeBlock}
            onClick={() => adjustFontSize("increase")}
            title={isFR ? "Augmenter la taille du texte" : "Increase text size"}
            aria-label={isFR ? "Augmenter la taille du texte" : "Increase text size"}
            style={{ minHeight: 36, padding: "6px 10px", fontWeight: 900 }}
          >
            A+
          </button>
        </ToolbarGroup>

        <ToolbarGroup label={isFR ? "Emphase" : "Emphasis"}>
          {[
            ["B", "bold", isFR ? "Gras" : "Bold"],
            ["I", "italic", isFR ? "Italique" : "Italic"],
            ["U", "underline", isFR ? "Souligné" : "Underline"],
            ...(enableHighlight
              ? [["H", "highlight", isFR ? "Surligner" : "Highlight"]]
              : []),
          ].map(([label, mark, title]) => (
            <button
              key={mark}
              type="button"
              className="ns-button ns-button-outline"
              disabled={disabled || !activeBlock}
              title={title}
              aria-label={title}
              onClick={() => applyInlineMark(mark)}
              style={{ minWidth: 38, minHeight: 36, padding: "6px 10px", fontWeight: 900 }}
            >
              {label}
            </button>
          ))}
        </ToolbarGroup>

        <ToolbarGroup label={isFR ? "Listes" : "Lists"}>
          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={disabled || !activeBlock}
            aria-pressed={manuscriptEditorBlockPresentation(activeBlock) === "bullet"}
            aria-label={isFR ? "Liste à puces" : "Bulleted list"}
            onClick={() => toggleList("bullet")}
            title={isFR ? "Liste à puces" : "Bulleted list"}
          >
            {isFR ? "• Puces" : "• Bullets"}
          </button>
          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={disabled || !activeBlock}
            aria-pressed={manuscriptEditorBlockPresentation(activeBlock) === "ordered"}
            aria-label={isFR ? "Liste numérotée" : "Numbered list"}
            onClick={() => toggleList("ordered")}
            title={isFR ? "Liste numérotée" : "Numbered list"}
          >
            {isFR ? "1. Numéros" : "1. Numbering"}
          </button>
        </ToolbarGroup>

        <ToolbarGroup label={isFR ? "Alignement" : "Alignment"}>
          {[
            ["left", isFR ? "Gauche" : "Left"],
            ["center", isFR ? "Centre" : "Centre"],
            ["right", isFR ? "Droite" : "Right"],
            ["justify", isFR ? "Justifié" : "Justify"],
          ].map(([alignment, label]) => (
            <button
              key={alignment}
              type="button"
              className="ns-button ns-button-outline"
              disabled={disabled || !activeBlock}
              aria-label={label}
              aria-pressed={activeBlock?.alignment === alignment}
              onClick={() => applyAlignment(alignment)}
              style={{ minHeight: 36, padding: "6px 10px" }}
            >
              {label}
            </button>
          ))}

          {enableWholeDocumentJustify ? (
            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={disabled}
              onClick={justifyWholeDocument}
              aria-pressed={
                wholeDocumentBodyJustified
              }
              aria-label={
                isFR
                  ? "Justifier toute la proposition"
                  : "Justify all"
              }
              title={
                isFR
                  ? "Justifier tous les paragraphes et listes de la proposition"
                  : "Justify every body paragraph and list item in this document"
              }
              style={{
                minHeight: 36,
                padding: "6px 10px",
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Tout justifier"
                : "Justify All"}
            </button>
          ) : null}
        </ToolbarGroup>

        <ToolbarGroup
          label={isFR ? "Paragraphe" : "Paragraph"}
          hidden={compactToolbar}
        >
          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={disabled || !activeBlock}
            onClick={() => applyIndent("outdent")}
            title={isFR ? "Diminuer le retrait" : "Decrease indent"}
            aria-label={isFR ? "Diminuer le retrait" : "Decrease indent"}
            style={{ minHeight: 36, padding: "6px 10px" }}
          >
            {isFR ? "Retrait −" : "Indent −"}
          </button>
          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={disabled || !activeBlock}
            onClick={() => applyIndent("indent")}
            title={isFR ? "Augmenter le retrait" : "Increase indent"}
            aria-label={isFR ? "Augmenter le retrait" : "Increase indent"}
            style={{ minHeight: 36, padding: "6px 10px" }}
          >
            {isFR ? "Retrait +" : "Indent +"}
          </button>

          <label
            style={{
              display: "grid",
              gap: 4,
              minWidth: 128,
            }}
          >
            <span
              className="ns-card-meta"
              style={{
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Interligne"
                : "Line spacing"}
            </span>

            <select
              aria-label={
                isFR
                  ? "Interligne"
                  : "Line spacing"
              }
              disabled={
                disabled ||
                !activeBlock
              }
              value={
                activeBlock
                  ?.lineSpacing ??
                ""
              }
              onChange={(event) =>
                applyLineSpacing(
                  event.target.value
                )
              }
              style={{
                minHeight: 36,
                borderRadius: 8,
                border:
                  "1px solid #cbd5e1",
                padding: "6px 9px",
                background: "#ffffff",
                font: "inherit",
              }}
            >
              <option value="">
                {isFR
                  ? "Par défaut du livre"
                  : "Book default"}
              </option>

              {MANUSCRIPT_LINE_SPACING_VALUES.map(
                (value) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {value === 1
                      ? "1.0"
                      : String(value)}
                  </option>
                )
              )}
            </select>
          </label>
        </ToolbarGroup>
      </div>

      <div
        ref={surfaceRef}
        className={`ns-card ns-nbb-manuscript-writing-surface${
          pageCanvasTrail
            ? " ns-nbb-manuscript-page-canvas-surface"
            : ""
        }`}
        data-manuscript-editor-surface="true"
        data-manuscript-continuous-editor="true"
        data-manuscript-working-page-guides={
          workingPageGuideIntervalPx > 0
            ? "true"
            : undefined
        }
        title={
          workingPageGuideIntervalPx > 0
            ? workingPageGuideLabel
            : undefined
        }
        contentEditable={!disabled}
        suppressContentEditableWarning
        aria-label={isFR ? "Manuscrit" : "Manuscript"}
        aria-multiline="true"
        onBeforeInput={handleSurfaceBeforeInput}
        onInput={handleSurfaceInput}
        onKeyDown={handleSurfaceKeyDown}
        onPaste={handleSurfacePaste}
        onMouseUp={rememberLiveSurfaceSelection}
        onKeyUp={rememberLiveSurfaceSelection}
        onClick={rememberLiveSurfaceSelection}
        style={{
          border:
            embeddedFlow
              ? 0
              : pageCanvasTrail
                ? "1px solid #94a3b8"
                : "1px solid rgba(234,88,12,0.30)",
          borderBottom:
            embeddedFlow
              ? 0
              : pageCanvasTrail
                ? 0
                : undefined,
          background:
            embeddedFlow
              ? "transparent"
              : pageCanvasTrail
                ? "#ffffff"
                : "#fffaf7",
          position:
            workingPageGuideIntervalPx > 0
              ? "relative"
              : undefined,
          boxShadow:
            embeddedFlow
              ? "none"
              : pageCanvasTrail
                ? "0 18px 34px rgba(15,23,42,0.10)"
                : "0 1px 3px rgba(15,23,42,0.08)",
          minHeight:
            embeddedFlow
              ? 0
              : pageCanvasTrail
                ? 430
                : 220,
          maxHeight:
            boundedSurface
              ? "62vh"
              : undefined,
          overflowY:
            boundedSurface
              ? "auto"
              : undefined,
          overscrollBehavior:
            boundedSurface
              ? "contain"
              : undefined,
          scrollBehavior:
            boundedSurface
              ? "smooth"
              : undefined,
          width:
            pageCanvasTrail
              ? "100%"
              : undefined,
          maxWidth:
            pageCanvasTrail
              ? 720
              : undefined,
          boxSizing:
            "border-box",
          marginLeft:
            pageCanvasTrail
              ? "auto"
              : undefined,
          marginRight:
            pageCanvasTrail
              ? "auto"
              : undefined,
          display:
            embeddedFlow
              ? "block"
              : flowEnabled
                ? "flow-root"
                : "grid",
          gap:
            embeddedFlow ||
            flowEnabled
              ? undefined
              : 3,
          padding:
            embeddedFlow
              ? 0
              : pageCanvasTrail
                ? "28px 34px 38px"
                : 14,
          borderRadius:
            embeddedFlow
              ? 0
              : pageCanvasTrail
                ? "14px 14px 0 0"
                : 12,
        }}
      >
        {workingPageGuideIntervalPx > 0 &&
        Math.floor(
          Number(
            workingPageGuideCount
          ) || 0
        ) > 1 ? (
          <div
            contentEditable={false}
            suppressContentEditableWarning
            aria-hidden="true"
            data-manuscript-working-page-guide-layer="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height:
                workingPageGuideIntervalPx *
                Math.max(
                  1,
                  Math.floor(
                    Number(
                      workingPageGuideCount
                    ) || 1
                  )
                ),
              pointerEvents:
                "none",
              zIndex: 4,
              overflow:
                "visible",
            }}
          >
            {Array.from({
              length:
                Math.max(
                  0,
                  Math.floor(
                    Number(
                      workingPageGuideCount
                    ) || 0
                  ) - 1
                ),
            }).map(
              (
                _,
                guideIndex
              ) => (
                <div
                  key={
                    `working-page-guide-${guideIndex + 1}`
                  }
                  data-manuscript-working-page-guide={
                    guideIndex + 1
                  }
                  style={{
                    position:
                      "absolute",
                    top:
                      workingPageGuideIntervalPx *
                      (
                        guideIndex +
                        1
                      ),
                    left: 0,
                    right: 0,
                    borderTop:
                      "2px dashed rgba(5, 150, 105, 0.58)",
                    pointerEvents:
                      "none",
                  }}
                >
                  <span
                    style={{
                      position:
                        "absolute",
                      right: 12,
                      top: -13,
                      padding:
                        "3px 8px",
                      borderRadius:
                        999,
                      border:
                        "1px solid rgba(5, 150, 105, 0.32)",
                      background:
                        "#f0fdf4",
                      color:
                        "#166534",
                      fontSize: 11,
                      fontWeight: 850,
                      lineHeight: 1.35,
                      whiteSpace:
                        "nowrap",
                      boxShadow:
                        "0 1px 3px rgba(15,23,42,0.08)",
                    }}
                  >
                    {isFR
                      ? `Fin de la page de travail ${guideIndex + 1}`
                      : `Working page ${guideIndex + 1} ends here`}
                  </span>
                </div>
              )
            )}
          </div>
        ) : null}

        {editableBlocks.map((block, index) => {
          const startsFigureFlow =
            flowEnabled &&
            block.type ===
              "paragraph" &&
            String(
              block.id || ""
            ) ===
              requestedFlowAnchor;

          if (startsFigureFlow) {
            figureFlowOpen = true;
          }

          const paragraphInFigureFlow =
            figureFlowOpen &&
            block.type ===
              "paragraph";

          const clearsFigureFlow =
            figureFlowOpen &&
            block.type !==
              "paragraph";

          if (clearsFigureFlow) {
            figureFlowOpen = false;
          }

          if (
            block.type === "list_item" &&
            block.list?.type === "ordered"
          ) {
            orderedIndex += 1;
          } else {
            orderedIndex = 0;
          }

          return (
            <React.Fragment
              key={block.id}
            >
              {startsFigureFlow ? (
                <span
                  contentEditable={false}
                  suppressContentEditableWarning
                  data-manuscript-flow-content="true"
                  style={{
                    display:
                      "contents",
                  }}
                >
                  {flowContent}
                </span>
              ) : null}

              <EditorBlock
                block={block}
                index={index}
                orderedIndex={orderedIndex}
                active={block.id === activeBlock?.id}
                isFR={isFR}
                registerRef={registerRef}
                onActivate={setActiveBlockId}
                onSelection={rememberSelection}
                paragraphInFigureFlow={
                  paragraphInFigureFlow
                }
                clearFigureFlow={
                  clearsFigureFlow
                }
                compactContinuousParagraphSpacing={
                  compactContinuousParagraphSpacing
                }
                workingPageBreak={
                  block.type ===
                    "page_break" &&
                  workingPageBreakBoundarySet
                    .has(
                      String(
                        block.id ||
                        ""
                      )
                    )
                }
                renderPageBreak={
                  renderPageBreak
                }
              />
            </React.Fragment>
          );
        })}
      </div>

      {pageCanvasTrail ? (
        <div
          className="ns-nbb-manuscript-page-canvas-trail"
          data-testid="manuscript-physical-page-canvas-trail"
          aria-label={
            pageCanvasLabel ||
            (
              isFR
                ? "Illustration de la page de travail"
                : "Writing-page illustration"
            )
          }
          style={{
            width:
              "100%",
            maxWidth:
              720,
            boxSizing:
              "border-box",
            margin:
              "-10px auto 8px",
            padding:
              "26px 30px 22px",
            border:
              "1px solid #94a3b8",
            borderTop:
              0,
            borderRadius:
              "0 0 14px 14px",
            background:
              "#ffffff",
            boxShadow:
              "0 12px 30px rgba(15,23,42,0.10)",
          }}
        >
          {pageCanvasTrail}
        </div>
      ) : null}

      <span
        className="ns-card-meta ns-nbb-manuscript-formatting-help"
        style={{ fontSize: 12, lineHeight: 1.5 }}
      >
        {compactToolbar
          ? (
              isFR
                ? `Utilisez le gras, l’italique, le soulignement, les listes et l’alignement pour le contenu. La police, la taille, le retrait et l’interligne suivent la mise en page du livre. Appuyez sur Entrée pour créer un nouveau paragraphe et sur Maj+Entrée pour un saut de ligne.`
                : `Use bold, italic, underline, lists, and alignment for the content. Font, text size, indentation, and line spacing follow the book design. Press Enter for a new paragraph and Shift+Enter for a line break.`
            )
          : (
              isFR
                ? `La police, la taille, le gras, l’italique et le soulignement s’appliquent au texte sélectionné. La structure, les listes, l’alignement, le retrait et l’interligne s’appliquent au paragraphe actif. Appuyez sur Entrée pour créer un nouveau paragraphe et sur Maj+Entrée pour un saut de ligne. Utilisez « ${resolvedSaveActionLabel} » avant d’actualiser ou de quitter la page.`
                : `Font, size, bold, italic, and underline apply to selected text. Structure, lists, alignment, indentation, and line spacing apply to the active paragraph. Press Enter for a new paragraph and Shift+Enter for a line break. Use ${resolvedSaveActionLabel} before refreshing or leaving the page.`
            )}
      </span>
    </div>
  );
}
